/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Prop, Vue} from 'vue-property-decorator'
import {AnalysisVO} from '@/valueObjects/AnalysisVO'
import {AnalysisErrorTypes, AnalysisErrorVO} from '@/valueObjects/AnalysisErrorVO'
import {displayLocalDate} from '@/utils/DateUtils'
import MessageBox from '@/components/shared/MessageBox'
import {Getter, Mutation} from 'vuex-class'

@Component({
    components: {
        MessageBox,
    },
})
export default class AnalysisSummary extends Vue {

    @Prop()
    private analysis: AnalysisVO

    @Prop()
    private isFinished: boolean

    @Getter('getStoreMessage') private storeMessage: string
    @Mutation('resetStoreMessage') private resetStoreMessage: any

    private message: string = ''

    public mounted() {

        if (this.storeMessage !== '') {
            // display store message coming from Home view children (e.g. used by NewAnalysis)
            this.message = this.storeMessage
            this.resetStoreMessage()
        }
    }

    private get staticAnalysisError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.static)
    }

    private get dynamicAnalysisError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.dynamic)
    }

    private get virusTotalError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.virusTotal)
    }

    private get pfavError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.pfav)
    }

    private get yaraAndroguardError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.yaraAndroguard)
    }

    private get yaraMemoryDumpError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.yaraMemoryDump)
    }

    private get yaraNetworkDumpError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.yaraNetwork)
    }

    private get yaraSourceCodeError(): string {
        return this.getErrorFromVO(AnalysisErrorTypes.yaraSourceCode)
    }

    private get isInteractive(): string {
        if (this.analysis.isInteractive) {
            return 'yes'
        }
        return 'no'
    }

    private get isInteractionConnectionAvailable(): boolean {
        if (this.analysis.isInteractive && this.analysis.interactiveConnection !== '') {
            return true
        }
        return false
    }

    private get displayInteractiveButton(): boolean {
        if (!this.analysis.isInteractive || this.analysis.dynamicAnalysisIsFinished) {
            return false
        }

        return true
    }

    private getErrorFromVO(type: string): string {
        const errorArray = this.analysis.errorReport
            .map((val) => AnalysisErrorVO.fromServiceFormat(val))
            .filter((val) => val.type === type)
        if (errorArray.length !== 0) {
            return errorArray[0].text
        }
    }

    private displayStatus(status: string): string {
        if (!status) {
            return ''
        }
        return status.replace('_', ' ')
    }

    private formatDate(date: string): string {
        return displayLocalDate(date)
    }

    private getClassName(status: string) {
        if (status === 'done') {
            return 'has-background-success'
        }

        if (status === 'not_started') {
            return 'has-background-info'
        }

        if (status === 'not_requested') {
            return 'has-background-grey'
        }

        if (status === 'in_progress') {
            return 'has-background-link'
        }

        return 'has-background-danger'
    }

    private clearMessage() {
        this.message = ''
    }

    private launchInteractiveSession() {
        // emit a signal to parent for analysis menu
        this.$router.push({name: 'analysis-interactive-session' })
    }
}
